import type { RouteObject } from "react-router-dom";
import { createBrowserRouter } from "react-router-dom";
import { wrapCreateBrowserRouter } from "@sentry/react";

import App from "../App";

import { routes } from "./routes";
import { path } from "./path";

import { ErrorPage } from "@pages/errorPage";
import type { IExtendedRouteObject } from "@routes/types.ts";
import { testRoutes } from "@routes/testRoutes/testRoutes";
import { guestJoinRoutes } from "@routes/guestJoin/guestJoinRoutes";
import { loginLoader, protectedLoader } from "@routes/authLoaders";
import { useUIStore } from "@/stores/uiStore";
import type { IRole } from "@/interfaces";
import { Root } from "@/root";

const withLayout: IExtendedRouteObject[] = [
  {
    route: {
      path: "/",
      element: <App />,
      loader: ({ request }) => {
        useUIStore
          .getState()
          .setPage(request, "pageTitle.main", [], "index", "main");
        return {};
      },
    },
    permissions: [
      "super-admin",
      "staff",
      "doctor",
      "patient",
      "local-admin",
      "admin",
    ],
  },
  ...routes,
];

const getExtractRolesRoute = (
  role: IRole | null,
  routes: IExtendedRouteObject[],
) => {
  return routes
    ?.filter(
      (route) =>
        role === null ||
        route.permissions?.includes(role) ||
        route.route.path === "/",
    )
    ?.map((route) => route.route);
};

const outsideRoutes: RouteObject[] = [
  {
    path: "/payment",
    lazy: () =>
      import(
        "@pages/panelPages/financialManagements/walletBalance/deposit/paymentResult"
      ),
    errorElement: <ErrorPage />,
  },
  {
    path: "/payment-success",
    lazy: () =>
      import(
        "@pages/panelPages/financialManagements/walletBalance/deposit/paymentSuccess"
      ),
    errorElement: <ErrorPage />,
  },
  {
    path: "/payment-fail",
    lazy: () =>
      import(
        "@pages/panelPages/financialManagements/walletBalance/deposit/paymentFail"
      ),
    errorElement: <ErrorPage />,
  },
  {
    path: "/auth",
    lazy: () => import("@pages/authManagement/authManagement"),
    loader: (props) => {
      useUIStore
        .getState()
        .setPage(props?.request, "pageTitle.login", [], "edit");
      return loginLoader(props);
    },
    errorElement: <ErrorPage />,
  },
  {
    path: path.authManagement.auth.route,
    lazy: () => import("@pages/authManagement/authManagement"),
    loader: (props) => {
      useUIStore
        .getState()
        .setPage(props?.request, "pageTitle.login", [], "edit");
      return loginLoader(props);
    },
    errorElement: <ErrorPage />,
  },
];

const sentryCreateBrowserRouter = wrapCreateBrowserRouter(createBrowserRouter);

export const getRouter = (role: IRole | null) => {
  return sentryCreateBrowserRouter([
    {
      path: "/",
      element: <Root />,
      children: [
        ...outsideRoutes,
        ...guestJoinRoutes,
        ...testRoutes,
        {
          lazy: () => import("@pages/Layout"),
          children: getExtractRolesRoute(role, [...withLayout]),
          loader: protectedLoader,
          // fullscreen error page
          errorElement: <ErrorPage />,
          // if a child doesn't provide an errorElement, it will default to this one
        },
      ],
      errorElement: <ErrorPage />,
    },
  ]);
};
